import React, { FC, useCallback, useEffect, useState } from "react";
import "./AppHome.scss";
import bg from "../../assets/img/wrapper.png";
import MainNavbar from "../../components/MainNavbar/MainNavbar";
import { Card, Col, Container, Row } from "react-bootstrap";
import useEmblaCarousel from "embla-carousel-react";
import { BvIcon } from "bevi-icon";
import { useMediaQuery } from "react-responsive";
import Footer from "../../components/Footer/Footer";
import { useAuth } from "../../providers/AuthProvider";
import Skeleton from "react-loading-skeleton";
import { useAgentsCategories } from "../../providers/Agents/AgentsCategories/AgentsCategoriesProvider";
import { AgentsCategoryModel } from "../../models/Agents/AgentsCategory/AgentsCategoryModel";
import { Link } from "react-router-dom";
import truncateText from "../../utils/TruncateText";
import { solicitarPermissaoLocalizacao } from "../../utils/deviceEvidence";
import ListArticlesView from "../../components/Articles/ListArticlesView/ListArticlesView";
import { useArticlesHighlights } from "../../providers/Articles/ArticlesHighlights/ArticlesHighlightsProvider";
import { useArticles } from "../../providers/Articles/ArticlesProvider";
import { useArticleComment } from "../../providers/Articles/ArticlesCommentsProvider";
import FormAppHome from "../../components/AppHome/FormAppHome/FormAppHome";
import { count } from "console";
import { useSidebarFilter } from "../../providers/SidebarFilterProvider";
import { useUsers } from "../../providers/Access/Users/UsersProvider";
import { UserRoleEnum } from "../../enums/User/UserRoleEnum";
import IALogo from "../../assets/img/IAwhite.svg";
import { useHomeBackground } from "../../hooks/useHomeBackground";

const HOME_BACKGROUND_FALLBACK = `${process.env.PUBLIC_URL}/assets/img/home-backgrounds/background-Juliane.png`;

interface AppHomeProps {}

const AppHome: FC<AppHomeProps> = () => {
  const [emblaRefOne, emblaApi] = useEmblaCarousel();
  const [emblaRefTwo] = useEmblaCarousel();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const randomBackground = useHomeBackground(!isMobile);
  const [prevDisabled, setPrevDisabled] = useState<boolean>(false);
  const [nextDisabled, setNextDisabled] = useState<boolean>(false);
  const [allCategories, setAllCategories] = useState<boolean>(false);

  const {
    articlesHighlights,
    isLoading: isLoadingHighlights,
    handleList: handleListHighlights,
  } = useArticlesHighlights();
  const {
    params: paramsAgentsCategories,
    setParams: setParamsAgentsCategories,
    categories,
    isLoading,
  } = useAgentsCategories();
  const { articlesMostRead, isLoadingMostRead, handleGetMostRead } =
    useArticles();
  const {
    articlesLastUpdates,
    isLoading: isLoadingLastUpdates,
    handleListUpdates,
  } = useArticleComment();
  const { userLogged } = useAuth();
  const { showSidebarFilter, setShowSidebarFilter } = useSidebarFilter();
  const { handleStatusTutorial } = useUsers();
  const [isWidgetCollapsed, setIsWidgetCollapsed] = useState<boolean>(false);
  const showIaWidget =
    !!userLogged &&
    (userLogged?.role === UserRoleEnum.ADMIN ||
      userLogged?.access_profile?.id !== 2);

  // No mobile o card aparece completo por 5s e depois encolhe para só o ícone.
  useEffect(() => {
    if (!isMobile || !showIaWidget) {
      setIsWidgetCollapsed(false);
      return;
    }
    const timer = setTimeout(() => setIsWidgetCollapsed(true), 5000);
    return () => clearTimeout(timer);
  }, [isMobile, showIaWidget]);

  useEffect(() => {
    handleListUpdates();
    handleGetMostRead();
    handleListHighlights();
    setParamsAgentsCategories({
      ...paramsAgentsCategories,
      withPaginate: false,
      filter: { only_active: true },
    });
    handleStatusTutorial();
    // AI-1277 — pede a localização logo na home, para o prompt do navegador não
    // aparecer no meio do fluxo de consulta. Negar não bloqueia nada.
    solicitarPermissaoLocalizacao();
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
    setTimeout(() => onSelect(), 0);
  }, [categories, emblaApi]);

  useEffect(() => {
    const hasMore = categories.length > 7;
    setAllCategories(hasMore);
  }, [categories]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;

    setPrevDisabled(!emblaApi.canScrollPrev());
    setNextDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (showSidebarFilter) {
      setShowSidebarFilter(false);
    }
  }, [showSidebarFilter]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const carousel = () => {
    return (
      <div className="embla">
        <div className="d-none d-md-flex mt-3 justify-content-center align-items-center me-2">
          <button
            onClick={scrollPrev}
            className="button-arrow"
            disabled={prevDisabled}
            style={{ opacity: prevDisabled ? "0.75" : "1" }}
          >
            <BvIcon
              name="arrowBack"
              variant="solid"
              weight={600}
              width={16}
              height={16}
              style={{ color: "#fff" }}
            />
          </button>
        </div>
        <div className="embla-viewport" ref={emblaRefOne}>
          <div className="embla-container">
            {allCategories && (
              <div className="embla-slide">
                <Link to={`/categories`}>
                  <Card style={{ backgroundColor: "#222343" }}>
                    <div className="card-title" style={{ color: "#fff" }}>
                      TODAS AS CATEGORIAS
                    </div>
                    <div className="card-text" style={{ color: "#fff" }}>
                      Acesse todas as categorias disponíveis no Bevi Ajuda.
                    </div>
                    <button
                      type="button"
                      className="learn-more"
                      style={{ color: "#25CBDB" }}
                    >
                      Saiba mais
                      <BvIcon
                        name="arrowForward"
                        variant="solid"
                        weight={600}
                        width={22}
                        height={22}
                        style={{ color: "#25CBDB" }}
                      />
                    </button>
                  </Card>
                </Link>
              </div>
            )}
            {categories
              .slice(0, 7)
              .map((slide: AgentsCategoryModel, index: number) => {
                return (
                  <React.Fragment key={slide.id?? index}>
                    {!isLoading ? (
                      <div className="embla-slide" key={index}>
                        <Link to={`/articles/categories/view/${slide.id}`}>
                          <Card>
                            <div
                              className={`card-title`}
                            >
                              {slide.name}
                            </div>
                            <div className="card-text">
                              {truncateText(slide?.description || "", 130)}
                            </div>
                            <button type="button" className="learn-more">
                              Saiba mais
                              <BvIcon
                                name="arrowForward"
                                variant="solid"
                                weight={600}
                                width={22}
                                height={22}
                              />
                            </button>
                          </Card>
                        </Link>
                      </div>
                    ) : (
                      <Skeleton
                        className="color-skeleton"
                        width={222}
                        height={227}
                        style={{ borderRadius: "32px" }}
                      />
                    )}
                  </React.Fragment>
                );
              })}

          </div>
        </div>
        <div className="d-none d-md-flex mt-3 justify-content-center align-items-center ms-2">
          <button
            onClick={scrollNext}
            className="button-arrow"
            disabled={nextDisabled}
            style={{ opacity: nextDisabled ? "0.75" : "1" }}
          >
            <BvIcon
              name="arrowForward"
              variant="solid"
              weight={600}
              width={16}
              height={16}
              style={{ color: "#fff" }}
            />
          </button>
        </div>
      </div>
    );
  };

  const renderLoadingSkeleton = () => {
    return (
      <Row>
        <Col>
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
        </Col>
        <Col>
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
          <Skeleton className="color-skeleton mt-2" width="100%" height={30} />
        </Col>
      </Row>
    );
  };

  const renderLastUpdates = () => {
    return (
      <Row className="justify-content-center mt-5">
        <Col md={10}>
          <h2 className="text-secondary">
            <b>Últimas atualizações</b>
          </h2>
          {!isLoadingLastUpdates ? (
            <ListArticlesView
              articles={articlesLastUpdates}
              viewMode="articles"
              viewInfo
            />
          ) : (
            <>{renderLoadingSkeleton()}</>
          )}
        </Col>
      </Row>
    );
  };

  const renderHighlights = () => {
    return (
      <Row className="justify-content-center mt-5">
        <Col md={10}>
          <h2 className="text-secondary">
            <b>Artigos destaques</b>
          </h2>
          {!isLoadingHighlights ? (
            <ListArticlesView
              articles={articlesHighlights}
              viewMode="articles"
            />
          ) : (
            <>{renderLoadingSkeleton()}</>
          )}
        </Col>
      </Row>
    );
  };

  const renderMostRead = () => {
    return (
      <Row className="justify-content-center mt-5">
        <Col md={10}>
          <h2 className="text-secondary">
            <b>Artigos mais lidos</b>
          </h2>
          {!isLoadingMostRead ? (
            <ListArticlesView articles={articlesMostRead} viewMode="articles" />
          ) : (
            <>{renderLoadingSkeleton()}</>
          )}
        </Col>
      </Row>
    );
  };

  return (
    <>
      <MainNavbar />
      <div className="AppHome" data-testid="AppHome">
        {(isMobile || randomBackground !== undefined) && (
          <img
            src={isMobile ? bg : randomBackground ?? HOME_BACKGROUND_FALLBACK}
            alt="Wrapper"
            className="home-page-background"
          />
        )}
        <div className="content">
          <div className="bevilearn-search-div">
            <div className="main-group-text">
              <Container className="mobile-center">
                <h4 className="title">Olá! Bem-vindo ao Bevi Ajuda.</h4>
                <h2 className={`sub-title ${userLogged ? "mb-5" : "mb-0"}`}>
                  Como podemos te ajudar?
                </h2>
                {userLogged && <FormAppHome />}
              </Container>
            </div>
          </div>
          <Container>
            {carousel()}

            <div className="wrapper">
              <Row className="mt-4">
                <Col md={12}>{renderLastUpdates()}</Col>
              </Row>
              <Row>
                <Col md={12}>{renderHighlights()}</Col>
              </Row>
              <Row>
                <Col md={12}>{renderMostRead()}</Col>
              </Row>
            </div>
          </Container>
        </div>
      </div>
      {showIaWidget && (
          <Link
            to="/chats/reply"
            aria-label="IA Beviajuda"
            className={`ia-beviajuda-widget${
              isWidgetCollapsed ? " ia-beviajuda-widget--collapsed" : ""
            }`}
          >
            <img
              src={IALogo}
              alt="Logo do Bevi ajuda com um simbulo de chat e a silha IA"
              className="ia-beviajuda-widget__icon"
            />
            <span className="ia-beviajuda-widget__label">
              Perguntar à IA Beviajuda
            </span>
            <p className="ia-beviajuda-widget__text">
              <strong>CLIQUE AQUI</strong> e tire suas dúvidas operacionais,
              simule, digite e consulte propostas com a{" "}
              <strong className="ia-beviajuda-widget__highlight">
                IA BeviAjuda.
              </strong>
            </p>
          </Link>
        )}
      <Footer fixed={true} />
    </>
  );
};

export default AppHome;
